import React, { useState, useEffect, useCallback } from 'react';
import { supplierOrderService, extractErrorDetails } from '../services/api';
import { useAuth } from '../App';
import ErrorDisplay from './ErrorDisplay';

function SupplierOrderList() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({
    orderNumber: '',
    proveedorId: '',
    status: '',
    fromDate: '',
    toDate: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
    page: 0,
    size: 20
  });
  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalElements: 0,
    first: true,
    last: true
  });
  const [expandedOrders, setExpandedOrders] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmedQuantities, setConfirmedQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');
  const [proveedores, setProveedores] = useState([]);

  const isDeposito = user?.role === 'ROLE_ADMIN_DEPOSITO';

  const loadProveedores = useCallback(async () => {
    try {
      const { proveedorService } = await import('../services/api');
      const response = await proveedorService.getActiveProveedores();
      if (response.success) {
        setProveedores(response.data || []);
      }
    } catch (err) {
      console.error('Error loading proveedores:', err);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await supplierOrderService.getSupplierOrdersWithFilters(filters);
      if (response.success && response.data) {
        setOrders(response.data.content || []);
        setPagination({
          totalPages: response.data.totalPages || 0,
          totalElements: response.data.totalElements || 0,
          first: response.data.first || false,
          last: response.data.last || false
        });
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar las órdenes');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadProveedores();
  }, [loadProveedores]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDIENTE':
        return 'Pendiente';
      case 'ENVIADA':
        return 'Enviada';
      case 'RECIBIDA':
        return 'Recibida';
      default:
        return status;
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const loadHistory = async (orderId) => {
    if (historyData[orderId]) {
      return;
    }
    
    setLoadingHistory(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await supplierOrderService.getSupplierOrderHistory(orderId);
      if (response.success) {
        setHistoryData(prev => ({ ...prev, [orderId]: response.data || [] }));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const toggleHistory = (orderId) => {
    if (!historyData[orderId]) {
      loadHistory(orderId);
    }
  };

  const getTotalItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + item.cantidad, 0);
  };

  const openConfirmModal = (order) => {
    const initialQuantities = {};
    order.items.forEach(item => {
      initialQuantities[item.id] = item.cantidad; // Default to ordered quantity
    });
    setConfirmedQuantities(initialQuantities);
    setConfirmModal(order);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
    setConfirmedQuantities({});
  };

  const handleQuantityChange = (itemId, value) => {
    if (value === '') {
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: ''
      }));
      return;
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: numValue
      }));
    }
  };

  const confirmReceived = async () => {
    if (!confirmModal) return;
    
    // Validate all quantities are filled
    const hasEmptyQuantity = confirmModal.items.some(
      item => confirmedQuantities[item.id] === '' || confirmedQuantities[item.id] === undefined
    );
    
    if (hasEmptyQuantity) {
      setError('Debe confirmar las cantidades de todos los productos');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const confirmedItems = confirmModal.items.map(item => ({
        itemId: item.id,
        cantidadConfirmada: parseInt(confirmedQuantities[item.id])
      }));
      
      const response = await supplierOrderService.updateSupplierOrderStatus(confirmModal.id, {
        status: 'RECIBIDA',
        confirmedItems: confirmedItems
      });
      
      if (response.success) {
        setSuccess('Orden marcada como recibida exitosamente');
        closeConfirmModal();
        loadOrders();
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al confirmar la orden');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setLoading(true);
      setError('');
      const response = await supplierOrderService.updateSupplierOrderStatus(orderId, {
        status: newStatus
      });
      
      if (response.success) {
        setSuccess('Estado actualizado exitosamente');
        loadOrders();
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al actualizar el estado');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('¿Está seguro que desea eliminar esta orden?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await supplierOrderService.deleteSupplierOrder(orderId);
      
      if (response.success) {
        setSuccess('Orden eliminada exitosamente');
        loadOrders();
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al eliminar la orden');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (orderId) => {
    try {
      await supplierOrderService.downloadSupplierOrderPdf(orderId);
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al descargar el PDF');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const handleExportExcel = async () => {
    try {
      await supplierOrderService.exportSupplierOrdersToExcel(filters);
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al exportar a Excel');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const handleExportCsv = async () => {
    try {
      await supplierOrderService.exportSupplierOrdersToCsv(filters);
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al exportar a CSV');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 0 // Reset page when filters change
    }));
  };

  const changePage = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  return (
    <div className="supplier-order-list">
      <h2>Órdenes al Proveedor</h2>
      
      {error && <ErrorDisplay message={error} errorTrace={errorTrace} />}
      {success && <div className="success-message">{success}</div>}
      
      {/* Filters */}
      <div className="order-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Número de Orden</label>
            <input
              type="number"
              placeholder="Ej: 123"
              value={filters.orderNumber}
              onChange={(e) => updateFilter('orderNumber', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>Proveedor</label>
            <select
              value={filters.proveedorId}
              onChange={(e) => updateFilter('proveedorId', e.target.value)}
            >
              <option value="">Todos</option>
              {proveedores.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Estado</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="ENVIADA">Enviada</option>
              <option value="RECIBIDA">Recibida</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Desde</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => updateFilter('fromDate', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>Hasta</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => updateFilter('toDate', e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>Ordenar por</label>
            <select
              value={filters.sortBy}
              onChange={(e) => updateFilter('sortBy', e.target.value)}
            >
              <option value="createdAt">Fecha de creación</option>
              <option value="statusChangedAt">Cambio de estado</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Dirección</label>
            <select
              value={filters.sortDirection}
              onChange={(e) => updateFilter('sortDirection', e.target.value)}
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>&nbsp;</label>
            <button 
              onClick={loadOrders}
              className="btn btn-primary"
              disabled={loading}
            >
              🔍 Buscar
            </button>
          </div>
        </div>
      </div>
      
      {/* Export buttons */}
      <div className="header-actions export-actions">
        <button onClick={handleExportExcel} className="btn btn-secondary">
          📊 Exportar a Excel
        </button>
        <button onClick={handleExportCsv} className="btn btn-secondary">
          📄 Exportar a CSV
        </button>
      </div>
      
      {/* Orders list */}
      {loading && <div className="loading">Cargando órdenes...</div>}
      
      {!loading && orders.length === 0 && (
        <div className="empty-state">
          <p>No hay órdenes para mostrar</p>
        </div>
      )}
      
      {!loading && orders.length > 0 && (
        <>
          <div className="orders-list">
            {orders.map(order => (
              <div key={order.id} className="order-card">
                <div className="order-header" onClick={() => toggleExpand(order.id)}>
                  <div className="order-main-info">
                    <span className="order-id">Orden #{order.id}</span>
                    <span className={`status-badge status-${order.status.toLowerCase()}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  <div className="order-summary">
                    <span className="order-items-count">
                      {order.items?.length || 0} producto(s) - {getTotalItems(order.items)} unidad(es)
                    </span>
                    <span className="expand-icon">{expandedOrders[order.id] ? '▼' : '▶'}</span>
                  </div>
                </div>
                
                <div className="order-meta">
                  <span><strong>Proveedor:</strong> {order.proveedorNombre}</span>
                  <span>Creado: {formatDate(order.createdAt)}</span>
                </div>
                
                {order.statusChangedAt && (
                  <div className="order-status-date">
                    <span>Estado actualizado: {formatDate(order.statusChangedAt)}</span>
                  </div>
                )}
                
                <div className="order-description">
                  <strong>Descripción:</strong> {order.descripcion}
                </div>
                
                <div className="order-actions">
                  <button 
                    onClick={() => handleDownloadPdf(order.id)}
                    className="btn btn-small btn-primary"
                  >
                    📄 Descargar PDF
                  </button>
                  {isDeposito && order.status === 'PENDIENTE' && (
                    <>
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'ENVIADA')}
                        className="btn btn-small btn-success"
                      >
                        Enviar
                      </button>
                      <button 
                        onClick={() => deleteOrder(order.id)} 
                        className="btn btn-small btn-danger"
                      >
                        Eliminar
                      </button>
                    </>
                  )}
                  {isDeposito && order.status === 'ENVIADA' && (
                    <button 
                      onClick={() => openConfirmModal(order)}
                      className="btn btn-small btn-success"
                    >
                      Confirmar Recepción
                    </button>
                  )}
                </div>
                
                {expandedOrders[order.id] && (
                  <div className="order-items-detail">
                    <h4>Productos</h4>
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th>Código</th>
                          <th>Solicitado</th>
                          <th>Confirmado</th>
                          <th>Diferencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map(item => {
                          const diff = item.cantidadConfirmada !== null && item.cantidadConfirmada !== undefined
                            ? item.cantidadConfirmada - item.cantidad
                            : null;
                          return (
                            <tr key={item.id}>
                              <td>{item.nombreProducto}</td>
                              <td>{item.codigoBarras}</td>
                              <td>{item.cantidad}</td>
                              <td>{item.cantidadConfirmada !== null && item.cantidadConfirmada !== undefined ? item.cantidadConfirmada : '-'}</td>
                              <td className={diff !== null && diff !== 0 ? (diff > 0 ? 'diff-positive' : 'diff-negative') : ''}>
                                {diff !== null ? (diff > 0 ? `+${diff}` : diff) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    <div className="order-history-section">
                      <button 
                        onClick={() => toggleHistory(order.id)}
                        className="btn btn-small btn-secondary"
                      >
                        {historyData[order.id] ? 'Ocultar Historial' : 'Ver Historial de Estados'}
                      </button>
                      
                      {loadingHistory[order.id] && <p>Cargando historial...</p>}
                      
                      {historyData[order.id] && historyData[order.id].length > 0 && (
                        <div className="history-list">
                          <h4>Historial de Cambios de Estado</h4>
                          <table className="history-table">
                            <thead>
                              <tr>
                                <th>Fecha</th>
                                <th>Estado Anterior</th>
                                <th>Nuevo Estado</th>
                                <th>Usuario</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyData[order.id].map((entry, idx) => (
                                <tr key={idx}>
                                  <td>{formatDate(entry.changedAt)}</td>
                                  <td>{getStatusLabel(entry.previousStatus)}</td>
                                  <td>{getStatusLabel(entry.newStatus)}</td>
                                  <td>{entry.changedByNombre}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {historyData[order.id] && historyData[order.id].length === 0 && (
                        <p className="no-history">No hay cambios de estado registrados</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => changePage(filters.page - 1)}
              disabled={pagination.first}
            >
              ← Anterior
            </button>
            <span>Página {filters.page + 1} de {pagination.totalPages}</span>
            <button
              onClick={() => changePage(filters.page + 1)}
              disabled={pagination.last}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
      
      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') closeConfirmModal();
        }}>
          <div className="modal-content confirm-modal">
            <div className="modal-header">
              <h3>Confirmar Recepción - Orden #{confirmModal.id}</h3>
              <button onClick={closeConfirmModal} className="btn-close">&times;</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">Confirme las cantidades recibidas de cada producto:</p>
              
              <table className="confirm-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Código</th>
                    <th>Solicitado</th>
                    <th>Confirmado</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmModal.items.map(item => (
                    <tr key={item.id}>
                      <td>{item.nombreProducto}</td>
                      <td>{item.codigoBarras}</td>
                      <td>{item.cantidad}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="quantity-input"
                          value={confirmedQuantities[item.id]}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={closeConfirmModal} 
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-success"
                onClick={confirmReceived} 
                disabled={loading}
              >
                {loading ? 'Confirmando...' : 'Confirmar Recepción'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierOrderList;
